import React from 'react'
import cx from 'classnames'
import { Drawer, Paper, ButtonProps, ButtonBase } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import ArrowRight from '@mui/icons-material/ArrowRight'
import ArrowLeft from '@mui/icons-material/ArrowLeft'

type Alignment = 'left' | 'right'

type Props = {
  alignment: Alignment
  dockedWidth: number
  paperWidth?: number
  children: React.ReactNode
  onTogglerClick: () => void
  open: boolean
  hideToggler?: boolean
  paperBackgroundColor?: string
}

type StyleProps = {
  alignment: Alignment
  width: number
}

type DrawerStyleProps = {
  dockedWidth: number
  paperWidth: number
  backgroundColor?: string
}

export default function FoldableDrawer({
  open,
  dockedWidth,
  paperWidth = dockedWidth,
  alignment,
  onTogglerClick,
  hideToggler,
  paperBackgroundColor,
  children,
}: Props) {
  const styles = useDrawerStyles({ dockedWidth, paperWidth, backgroundColor: paperBackgroundColor })
  const isOverlaying = paperWidth > dockedWidth
  return (
    <>
      {!hideToggler && <Toggler open={open} alignment={alignment} width={dockedWidth} onClick={onTogglerClick} />}
      <Drawer
        variant="persistent"
        anchor={alignment}
        open={open}
        className={styles.drawer}
        SlideProps={{ mountOnEnter: false }}
        classes={{ paper: cx(styles.drawerPaper, { [styles.drawerPaperOverlaying]: isOverlaying }) }}
      >
        {children}
      </Drawer>
    </>
  )
}

type TogglerProps = StyleProps & ButtonProps & { open: boolean }

function Toggler({ open, width, alignment, onClick }: TogglerProps) {
  const styles = useTogglerStyles({ width, alignment })
  const [OpenArrow, ClosedArrow] = alignment === 'left' ? [ArrowLeft, ArrowRight] : [ArrowRight, ArrowLeft]
  return (
    <Paper square elevation={4} className={cx(styles.toggler, { [styles.togglerOpen]: open })}>
      <ButtonBase focusRipple onClick={onClick}>
        {open ? (
          <OpenArrow titleAccess={`${alignment} open`} color="action" />
        ) : (
          <ClosedArrow titleAccess={`${alignment} closed`} color="action" />
        )}
      </ButtonBase>
    </Paper>
  )
}

const marginProp = (alignment: Alignment) => (alignment === 'left' ? 'marginLeft' : 'marginRight')

/**
 * These styles can be applied to the content element to make it
 * swift when drawer is open. This way it won't stay behind it.
 */
export const useContentStyles = makeStyles((theme) => ({
  content: (props: StyleProps) => ({
    transition: theme.transitions.create(marginProp(props.alignment), {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [marginProp(props.alignment)]: -props.width,
  }),
  contentSwift: (props: StyleProps) => ({
    transition: theme.transitions.create(marginProp(props.alignment), {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [marginProp(props.alignment)]: 0,
  }),
}))

const useDrawerStyles = makeStyles((theme) => ({
  drawer: (props: DrawerStyleProps) => ({
    color: theme.palette.background.paper,
    width: props.dockedWidth,
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  drawerPaper: (props: DrawerStyleProps) => ({
    width: props.paperWidth,
    backgroundColor: props.backgroundColor,
    paddingTop: 56,
    paddingBottom: 15,
    overflow: 'hidden',
    transition: theme.transitions.create(['width', 'box-shadow'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
  drawerPaperOverlaying: {
    boxShadow: theme.shadows[8],
  },
}))

const useTogglerStyles = makeStyles((theme) => ({
  toggler: (props: StyleProps) => ({
    position: 'absolute',
    zIndex: theme.zIndex.drawer - 1,
    top: '50%',
    [props.alignment]: 0,
    background: theme.palette.background.paper,
    transition: theme.transitions.create(props.alignment, {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  }),
  togglerOpen: (props: StyleProps) => ({
    transition: theme.transitions.create(props.alignment, {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    [props.alignment]: props.width,
  }),
}))
